const KOKO_CART_RECOMMENDATION_LIMIT = 4;

async function copyKokoCartCoupon(button) {
  const code = button.dataset.cartCouponCodeValue;
  let copied = false;
  try {
    await navigator.clipboard.writeText(code);
    copied = true;
  } catch (error) {
    const input = document.createElement('textarea');
    input.value = code;
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    try {
      copied = document.execCommand('copy');
    } catch (fallbackError) {
      copied = false;
    }
    input.remove();
  }

  if (!copied) {
    button.textContent = button.dataset.selectText || 'Select code';
    const codeText = button.closest('[data-cart-coupon]')?.querySelector('[data-cart-coupon-code]');
    if (codeText) {
      const range = document.createRange();
      range.selectNodeContents(codeText);
      window.getSelection()?.removeAllRanges();
      window.getSelection()?.addRange(range);
    }
    return;
  }

  button.textContent = button.dataset.copiedText || 'Copied';
  button.setAttribute('aria-label', button.dataset.copiedLabel || `Coupon code ${code} copied`);
  window.setTimeout(() => {
    button.textContent = button.dataset.copyText || 'Copy code';
    button.setAttribute('aria-label', button.dataset.copyLabel || `Copy coupon code ${code}`);
  }, 1500);
}

function getKokoRecommendationsUrl(productId) {
  const root = window.Shopify?.routes?.root || '/';
  return `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${KOKO_CART_RECOMMENDATION_LIMIT}&intent=related`;
}

function getKokoFallbackProductsUrl() {
  const root = window.Shopify?.routes?.root || '/';
  return `${root}collections/all/products.json?limit=12`;
}

function getKokoProductImage(product) {
  const image = product.featured_image || product.images?.[0];
  if (!image) return '';
  if (typeof image === 'string') return image;
  return image.url || image.src || '';
}

function getKokoVariantId(product) {
  const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
  return variant?.id;
}

function escapeKokoCartHtml(value) {
  return String(value || '').replace(/[&<>"']/g, (character) => {
    const replacements = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return replacements[character];
  });
}

function formatKokoCartMoney(cents) {
  if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
  const amount = Number(cents || 0) / 100;
  return `$${amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)}`;
}

async function renderKokoCartRecommendations() {
  const root = document.querySelector('[data-koko-cart-recommendations]');
  if (!root) return;

  const cartItems = Array.from(document.querySelectorAll('cart-drawer .cart-item[data-product-id]'));
  if (!cartItems.length) {
    root.hidden = true;
    root.innerHTML = '';
    return;
  }

  const productId = cartItems[0].dataset.productId;
  const inCart = new Set(cartItems.map((item) => item.dataset.productId));
  if (!productId) return;

  try {
    const response = await fetch(getKokoRecommendationsUrl(productId));
    const data = await response.json();
    let products = (data.products || [])
      .filter((product) => !inCart.has(String(product.id)))
      .slice(0, KOKO_CART_RECOMMENDATION_LIMIT);

    if (!products.length) {
      const fallbackResponse = await fetch(getKokoFallbackProductsUrl());
      const fallbackData = await fallbackResponse.json();
      products = (fallbackData.products || [])
        .filter((product) => !inCart.has(String(product.id)))
        .slice(0, KOKO_CART_RECOMMENDATION_LIMIT);
    }

    if (!products.length) {
      root.hidden = true;
      root.innerHTML = '';
      return;
    }

    root.hidden = false;
    root.innerHTML = `
      <h3>You May Also Like</h3>
      <div class="koko-cart-recommendations__row">
        ${products
          .map((product) => {
            const variantId = getKokoVariantId(product);
            const image = getKokoProductImage(product);
            if (!variantId) return '';
            const imageMarkup = image
              ? `<img src="${escapeKokoCartHtml(image)}" alt="${escapeKokoCartHtml(product.title)}" loading="lazy">`
              : '';
            const productUrl = escapeKokoCartHtml(product.url);
            const productTitle = escapeKokoCartHtml(product.title);
            return `
              <article class="koko-cart-recommendation">
                <a class="koko-cart-recommendation__media" href="${productUrl}">
                  ${imageMarkup}
                </a>
                <a class="koko-cart-recommendation__title" href="${productUrl}">${productTitle}</a>
                <div class="koko-cart-recommendation__price">${formatKokoCartMoney(product.price)}</div>
                <button type="button" class="koko-cart-recommendation__add" data-koko-recommendation-add="${variantId}">+ Add</button>
              </article>
            `;
          })
          .join('')}
      </div>
    `;
  } catch (error) {
    console.error(error);
    root.hidden = true;
  }
}

async function addKokoCartRecommendation(variantId, button) {
  if (!variantId) return;

  button?.setAttribute('aria-disabled', 'true');
  const cartDrawer = document.querySelector('cart-drawer');
  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  config.headers['Content-Type'] = 'application/json';
  config.body = JSON.stringify({
    id: variantId,
    quantity: 1,
    sections: cartDrawer?.getSectionsToRender().map((section) => section.id) || [],
    sections_url: window.location.pathname,
  });

  try {
    const response = await fetch(`${routes.cart_add_url}`, config).then((result) => result.json());
    if (response.status) return;
    cartDrawer?.renderContents(response);
  } finally {
    button?.removeAttribute('aria-disabled');
  }
}

function getKokoCartEditRoot(element) {
  return element?.closest('[data-koko-cart-edit-root]');
}

let kokoCartEditActiveRoot = null;
let kokoCartEditUpdating = false;
const kokoCartEditPendingKey = 'koko-cart-edit-pending-v1';
let kokoCartEditReturnFocus = null;
let kokoCartEditMediaItems = [];
let kokoCartEditMediaIndex = 0;
let kokoCartEditMediaTarget = null;
let kokoCartEditSwatchStyles = new Map();
const kokoCartEditSingleGallery = window.matchMedia('(max-width: 1199px)');

function getKokoCartEditModal() {
  return document.querySelector('[data-koko-cart-edit-modal]');
}

function getKokoCartEditModalContent() {
  return getKokoCartEditModal()?.querySelector('[data-koko-cart-edit-modal-content]');
}

function getKokoCartEditJson(root, selector) {
  const script = root?.querySelector(selector);
  if (!script) return [];
  try {
    return JSON.parse(script.textContent || '[]');
  } catch (error) {
    console.error(error);
    return [];
  }
}

function getKokoCartVariants(root) {
  return getKokoCartEditJson(root, '[data-koko-cart-edit-variants]');
}

function getKokoCartOptions(root) {
  return getKokoCartEditJson(root, '[data-koko-cart-edit-options]');
}

function getKokoCartSelectedOptions(root) {
  const selected = [];
  const modal = getKokoCartEditModal();
  const scope = root === kokoCartEditActiveRoot && modal && !modal.hidden ? modal : root;
  scope.querySelectorAll('[data-koko-cart-edit-option]').forEach((option) => {
    const position = Number(option.dataset.optionPosition);
    const value = option.querySelector('[data-koko-cart-edit-value].is-selected')?.dataset.optionValue;
    if (Number.isInteger(position) && value) selected[position] = value;
  });
  return selected;
}

function findKokoCartSelectedVariant(root) {
  const variants = getKokoCartVariants(root);
  const selectedOptions = getKokoCartSelectedOptions(root);
  if (!selectedOptions.length) {
    return variants.find((variant) => String(variant.id) === root.dataset.currentVariantId) || variants[0];
  }

  return variants.find((variant) => {
    const options = variant.options || [];
    return selectedOptions.every((value, index) => String(options[index]) === String(value));
  });
}

function setKokoCartEditStatus(root, message = '') {
  const modal = getKokoCartEditModal();
  const status =
    root === kokoCartEditActiveRoot && modal && !modal.hidden
      ? modal.querySelector('[data-koko-cart-edit-status]') || modal.querySelector('[data-koko-cart-edit-modal-status]')
      : root?.querySelector('[data-koko-cart-edit-status]');
  if (status) status.textContent = message;
}

function syncKokoCartEditState(root) {
  const variant = findKokoCartSelectedVariant(root);
  const modal = getKokoCartEditModal();
  const updateButton =
    root === kokoCartEditActiveRoot && modal && !modal.hidden
      ? modal.querySelector('[data-koko-cart-edit-update]')
      : root.querySelector('[data-koko-cart-edit-update]');
  const price = modal?.querySelector('[data-koko-cart-edit-price]');
  root.dataset.selectedVariantId = variant?.id ? String(variant.id) : '';
  if (price && Number.isFinite(Number(variant?.price))) price.innerHTML = formatKokoCartMoney(variant.price);
  if (modal && root === kokoCartEditActiveRoot) {
    const productUrl = getKokoCartEditProductUrl(root, true);
    modal.querySelectorAll('.koko-cart-edit__title, .koko-cart-edit__details').forEach((link) => {
      link.setAttribute('href', productUrl);
    });
  }

  if (!updateButton) return;

  const pending = readKokoCartEditPending();
  const disabled = kokoCartEditUpdating || (!pending && (!variant || variant.available === false));
  modal?.querySelectorAll('[data-koko-cart-edit-value]').forEach((control) => {
    control.disabled = kokoCartEditUpdating || Boolean(pending);
  });
  updateButton.disabled = disabled;
  const reviewLink = modal?.querySelector('[data-koko-cart-edit-review]');
  if (reviewLink) reviewLink.hidden = !pending || kokoCartEditUpdating;
  updateButton.textContent = (kokoCartEditUpdating ? root.dataset.textUpdating : pending ? root.dataset.textCheck : root.dataset.textUpdate) || updateButton.textContent;
  setKokoCartEditStatus(root, kokoCartEditUpdating ? root.dataset.textUpdating : pending ? (pending.phase === 'added' ? root.dataset.textPending : root.dataset.textReview) : disabled ? root.dataset.textSelectAvailable || '' : '');
}

function getKokoCartEditProductUrl(root, useSelectedVariant = false) {
  const url = new URL(root?.dataset.productUrl || window.location.pathname, window.location.origin);
  const variantId = useSelectedVariant ? root?.dataset.selectedVariantId : root?.dataset.currentVariantId;
  if (variantId) url.searchParams.set('variant', variantId);
  return url.toString();
}

function stripKokoDuplicateIds(element) {
  element?.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
  return element;
}

function normalizeKokoCartEditToken(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/&amp;/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function isKokoCartColorOptionName(name) {
  return /colou?r/i.test(String(name || ''));
}

function getKokoCartColorOption(root) {
  return getKokoCartOptions(root).find((option) => isKokoCartColorOptionName(option.name));
}

function getKokoCartImageKey(src) {
  if (!src) return '';
  try {
    const url = new URL(src, window.location.origin);
    return `${url.hostname}${url.pathname}`;
  } catch (error) {
    return String(src).split('?')[0];
  }
}

function getKokoCartEditMediaItems(root, productInfo) {
  const mediaWrapper = productInfo?.querySelector('.product__media-wrapper');
  const productImages = mediaWrapper?.querySelectorAll('.product__media-list .product__media-item img');
  const images = productImages?.length ? productImages : mediaWrapper?.querySelectorAll('img');
  const seen = new Set();
  const mediaItems = [];

  images?.forEach((image) => {
    const src = image.currentSrc || image.src || image.getAttribute('src') || '';
    const key = getKokoCartImageKey(src);
    if (!src || seen.has(key)) return;
    seen.add(key);
    const mediaNode = image.closest('[data-media-id]');
    const mediaId = String(mediaNode?.dataset.mediaId || '').split('-').pop();
    mediaItems.push({
      alt: image.getAttribute('alt') || root.dataset.productTitle || '',
      mediaId,
      sizes: image.getAttribute('sizes') || '',
      src,
      srcset: image.getAttribute('srcset') || '',
    });
  });

  if (mediaItems.length) return mediaItems;
  if (!root.dataset.imageUrl) return [];
  return [
    {
      alt: root.dataset.imageAlt || root.dataset.productTitle || '',
      mediaId: '',
      sizes: '',
      src: root.dataset.imageUrl,
      srcset: '',
    },
  ];
}

function getKokoCartEditColorMediaIndex(root, colorValue, mediaItems = kokoCartEditMediaItems) {
  const colorOption = getKokoCartColorOption(root);
  const colorPosition = Number(colorOption?.position);
  const normalizedColor = normalizeKokoCartEditToken(colorValue);
  const variants = getKokoCartVariants(root);
  const variant = variants.find((item) => {
    if (!Number.isInteger(colorPosition)) return false;
    return normalizeKokoCartEditToken(item.options?.[colorPosition]) === normalizedColor;
  });
  const featuredMediaId = String(variant?.featured_media_id || '');
  const featuredImageKey = getKokoCartImageKey(variant?.featured_image || '');

  if (featuredMediaId) {
    const mediaIndex = mediaItems.findIndex((item) => String(item.mediaId) === featuredMediaId);
    if (mediaIndex >= 0) return mediaIndex;
  }

  if (featuredImageKey) {
    const mediaIndex = mediaItems.findIndex((item) => getKokoCartImageKey(item.src) === featuredImageKey);
    if (mediaIndex >= 0) return mediaIndex;
  }

  if (normalizedColor) {
    const mediaIndex = mediaItems.findIndex((item) => normalizeKokoCartEditToken(item.alt).includes(normalizedColor));
    if (mediaIndex >= 0) return mediaIndex;
  }

  return 0;
}

function getKokoCartEditPdpSwatchStyle(styleValue) {
  const background = String(styleValue || '').match(/--swatch--background\s*:\s*([^;]+)/i)?.[1]?.trim();
  if (!background) return '';
  if (/^url\(/i.test(background)) return `--koko-swatch-image: ${background};`;
  return `--koko-swatch-color: ${background};`;
}

function getKokoCartEditPdpSwatchStyles(productInfo) {
  const swatchStyles = new Map();
  const labels = Array.from(productInfo?.querySelectorAll('label[for]') || []);

  productInfo?.querySelectorAll('.product-form__input--swatch input.swatch-input__input').forEach((input) => {
    const value = input.getAttribute('value') || '';
    const label = labels.find((item) => item.getAttribute('for') === input.getAttribute('id'));
    const swatchStyle = getKokoCartEditPdpSwatchStyle(label?.querySelector('.swatch')?.getAttribute('style'));
    if (value && swatchStyle) swatchStyles.set(normalizeKokoCartEditToken(value), swatchStyle);
  });

  return swatchStyles;
}

function getKokoCartCurrentColor(root) {
  const colorOption = getKokoCartColorOption(root);
  if (!colorOption) return '';
  const pending = readKokoCartEditPending();
  if (pending && String(pending.source.variant_id) === root.dataset.currentVariantId) {
    return pending.options?.[colorOption.position] || '';
  }
  const modal = getKokoCartEditModal();
  const selectedButton =
    root === kokoCartEditActiveRoot && modal && !modal.hidden
      ? modal.querySelector(
          `[data-koko-cart-edit-option][data-option-position="${Number(colorOption.position)}"] [data-koko-cart-edit-value].is-selected`
        )
      : null;
  return selectedButton?.dataset.optionValue || colorOption.selected || colorOption.values?.[0] || '';
}

function renderKokoCartEditMediaSlides(mediaItems, startIndex) {
  if (!mediaItems.length) return '';
  const mobile = kokoCartEditSingleGallery.matches;
  const visibleCount = mobile ? mediaItems.length : Math.min(2, mediaItems.length);
  return Array.from({ length: visibleCount })
    .map((_, offset) => {
      const mediaItem = mediaItems[mobile ? offset : (startIndex + offset) % mediaItems.length];
      const srcset = mediaItem.srcset ? ` srcset="${escapeKokoCartHtml(mediaItem.srcset)}"` : '';
      const sizes = mediaItem.sizes ? ` sizes="${escapeKokoCartHtml(mediaItem.sizes)}"` : '';
      return `
        <figure class="koko-cart-edit-modal__media-slide" data-koko-cart-edit-media-slide>
          <img
            src="${escapeKokoCartHtml(mediaItem.src)}"
            alt="${escapeKokoCartHtml(mediaItem.alt)}"
            loading="${offset === (mobile ? startIndex : 0) ? 'eager' : 'lazy'}"
            ${srcset}
            ${sizes}
          >
        </figure>
      `;
    })
    .join('');
}

function renderKokoCartEditMediaGallery(root, productInfo) {
  kokoCartEditMediaTarget = null;
  kokoCartEditMediaItems = getKokoCartEditMediaItems(root, productInfo);
  kokoCartEditMediaIndex = getKokoCartEditColorMediaIndex(root, getKokoCartCurrentColor(root), kokoCartEditMediaItems);

  if (!kokoCartEditMediaItems.length) return '';
  return `
    <div
      class="koko-cart-edit-modal__media-gallery"
      data-koko-cart-edit-media-gallery
      data-koko-cart-edit-media-index="${kokoCartEditMediaIndex}"
    >
      <button type="button" class="koko-cart-edit-modal__media-nav koko-cart-edit-modal__media-nav--prev" data-koko-cart-edit-media-prev aria-label="Previous image" ${kokoCartEditMediaItems.length < 2 ? 'hidden' : ''}>
        <span aria-hidden="true">&#8249;</span>
      </button>
      <div class="koko-cart-edit-modal__media-track" data-koko-cart-edit-media-track data-gallery-mode="${kokoCartEditSingleGallery.matches ? 'single' : 'pair'}">
        ${renderKokoCartEditMediaSlides(kokoCartEditMediaItems, kokoCartEditMediaIndex)}
      </div>
      <button type="button" class="koko-cart-edit-modal__media-nav koko-cart-edit-modal__media-nav--next" data-koko-cart-edit-media-next aria-label="Next image" ${kokoCartEditMediaItems.length < 2 ? 'hidden' : ''}>
        <span aria-hidden="true">&#8250;</span>
      </button>
    </div>
  `;
}

function updateKokoCartEditMediaGallery(root, nextIndex = kokoCartEditMediaIndex, behavior = 'auto') {
  const modal = getKokoCartEditModal();
  const gallery = modal?.querySelector('[data-koko-cart-edit-media-gallery]');
  const track = modal?.querySelector('[data-koko-cart-edit-media-track]');
  if (!gallery || !track || !kokoCartEditMediaItems.length || root !== kokoCartEditActiveRoot) return;

  const mediaCount = kokoCartEditMediaItems.length;
  kokoCartEditMediaIndex = ((nextIndex % mediaCount) + mediaCount) % mediaCount;
  gallery.dataset.kokoCartEditMediaIndex = String(kokoCartEditMediaIndex);
  kokoCartEditMediaTarget = behavior === 'smooth' ? kokoCartEditMediaIndex : null;
  if (kokoCartEditSingleGallery.matches) {
    const slide = track.children[kokoCartEditMediaIndex];
    if (slide) track.scrollTo({ left: slide.offsetLeft - track.firstElementChild.offsetLeft, behavior });
  } else {
    track.innerHTML = renderKokoCartEditMediaSlides(kokoCartEditMediaItems, kokoCartEditMediaIndex);
  }
}

function initKokoCartEditMediaGallery(root) {
  const track = getKokoCartEditModal()?.querySelector('[data-koko-cart-edit-media-track]');
  if (!track) return;
  const syncMediaIndex = () => {
    if (!kokoCartEditSingleGallery.matches || root !== kokoCartEditActiveRoot || track.children.length < 2) return;
    // Ignore scroll events from the desktop pair until the full track is rebuilt.
    if (track.dataset.galleryMode !== 'single') return;
    const step = track.children[1].offsetLeft - track.children[0].offsetLeft;
    if (!step) return;
    // Keep rapid arrow clicks relative to their destination during smooth scrolling.
    if (kokoCartEditMediaTarget !== null) {
      const targetLeft = track.children[kokoCartEditMediaTarget].offsetLeft - track.children[0].offsetLeft;
      if (Math.abs(track.scrollLeft - targetLeft) > 1) return;
      kokoCartEditMediaTarget = null;
    }
    kokoCartEditMediaIndex = Math.max(0, Math.min(track.children.length - 1, Math.round(track.scrollLeft / step)));
    track.closest('[data-koko-cart-edit-media-gallery]').dataset.kokoCartEditMediaIndex = String(kokoCartEditMediaIndex);
  };
  track.addEventListener('scroll', syncMediaIndex, { passive: true });
  const interruptNavigation = () => {
    kokoCartEditMediaTarget = null;
    syncMediaIndex();
  };
  track.addEventListener('pointerdown', interruptNavigation, { passive: true });
  track.addEventListener('wheel', interruptNavigation, { passive: true });
  const initialIndex = kokoCartEditMediaIndex;
  updateKokoCartEditMediaGallery(root, initialIndex);
}

kokoCartEditSingleGallery.addEventListener('change', () => {
  const root = kokoCartEditActiveRoot;
  const track = getKokoCartEditModal()?.querySelector('[data-koko-cart-edit-media-track]');
  if (!root || !track) return;
  const index = kokoCartEditMediaIndex;
  track.dataset.galleryMode = kokoCartEditSingleGallery.matches ? 'single' : 'pair';
  track.innerHTML = renderKokoCartEditMediaSlides(kokoCartEditMediaItems, index);
  updateKokoCartEditMediaGallery(root, index);
});

function getKokoCartEditPriceMarkup(root, productInfo) {
  const price = productInfo?.querySelector('[id^="price-"] .price, .price--large, .price')?.cloneNode(true);
  if (!price) return escapeKokoCartHtml(root.dataset.productPrice || '');
  stripKokoDuplicateIds(price);
  return price.outerHTML;
}

function getKokoCartEditTitle(root, productInfo) {
  return (
    productInfo?.querySelector('.product__title h1, .product__title h2, h1')?.textContent?.trim() ||
    root.dataset.productTitle ||
    ''
  );
}

function getKokoCartSwatchColor(value) {
  const token = normalizeKokoCartEditToken(value);
  const colors = [
    ['black', '#222222'],
    ['white', '#ffffff'],
    ['ivory', '#f6f0e7'],
    ['cream', '#f6f0e7'],
    ['beige', '#d8c8b0'],
    ['brown', '#6b5148'],
    ['green', '#6f8a68'],
    ['blue', '#7fb7d7'],
    ['navy', '#1f2f55'],
    ['red', '#9f263f'],
    ['pink', '#e8a1b4'],
    ['yellow', '#f3d96b'],
    ['orange', '#d88745'],
    ['purple', '#8c5a9e'],
    ['gray', '#9a9a9a'],
    ['grey', '#9a9a9a'],
    ['gold', '#c3a34e'],
    ['silver', '#c7c7c7'],
  ];
  return colors.find(([name]) => token.includes(name))?.[1] || '#d7d0c7';
}

function getKokoCartSwatchStyle(root, value) {
  const pdpSwatchStyle = kokoCartEditSwatchStyles.get(normalizeKokoCartEditToken(value));
  if (pdpSwatchStyle) return pdpSwatchStyle;

  const mediaIndex = getKokoCartEditColorMediaIndex(root, value);
  const mediaItem = kokoCartEditMediaItems[mediaIndex];
  if (mediaItem?.src) return `--koko-swatch-image: url('${escapeKokoCartHtml(mediaItem.src)}');`;
  return `--koko-swatch-color: ${getKokoCartSwatchColor(value)};`;
}

function renderKokoCartEditOption(root, option) {
  const values = Array.isArray(option.values) ? option.values : [];
  const pending = readKokoCartEditPending();
  const recovering = pending && String(pending.source.variant_id) === root.dataset.currentVariantId;
  const selectedValue = (recovering ? pending.options?.[option.position] : '') || option.selected || values[0] || '';
  const isColor = isKokoCartColorOptionName(option.name);
  return `
    <fieldset
      class="koko-cart-edit__option${isColor ? ' koko-cart-edit__option--color' : ''}"
      data-koko-cart-edit-option
      data-option-position="${Number(option.position)}"
      data-option-name="${escapeKokoCartHtml(option.name)}"
    >
      <legend>
        <span>${escapeKokoCartHtml(option.name)}</span>
        <strong data-koko-cart-edit-selected>${escapeKokoCartHtml(selectedValue)}</strong>
      </legend>
      <div class="koko-cart-edit__values">
        ${values
          .map((value) => {
            const label = String(value);
            const isSelected = label === String(selectedValue);
            const swatchStyle = isColor ? ` style="${getKokoCartSwatchStyle(root, label)}"` : '';
            return `
              <button
                type="button"
                class="koko-cart-edit__value${isColor ? ' koko-cart-edit__swatch' : ''}${isSelected ? ' is-selected' : ''}"
                data-koko-cart-edit-value
                ${isColor ? 'data-koko-cart-edit-color-index' : ''}
                data-option-position="${Number(option.position)}"
                data-option-name="${escapeKokoCartHtml(option.name)}"
                data-option-value="${escapeKokoCartHtml(label)}"
                aria-pressed="${String(isSelected)}"
                aria-label="${escapeKokoCartHtml(label)}"
                ${swatchStyle}
              >
                <span class="${isColor ? 'visually-hidden' : ''}">${escapeKokoCartHtml(label)}</span>
              </button>
            `;
          })
          .join('')}
      </div>
    </fieldset>
  `;
}

function renderKokoCartEditOptions(root) {
  const options = getKokoCartOptions(root);
  if (!options.length) return '';
  return `<div class="koko-cart-edit__options">${options.map((option) => renderKokoCartEditOption(root, option)).join('')}</div>`;
}

function renderKokoCartEditLoading(root) {
  const content = getKokoCartEditModalContent();
  if (!content) return;
  content.innerHTML = `
    <p class="koko-cart-edit-modal__status" data-koko-cart-edit-modal-status role="status">
      ${escapeKokoCartHtml(root?.dataset.textLoading || '')}
    </p>
  `;
}

async function fetchKokoCartEditProduct(root) {
  const response = await fetch(getKokoCartEditProductUrl(root));
  if (!response.ok) throw new Error(`Unable to load product-info for cart edit: ${response.status}`);
  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const productInfo = doc.querySelector('product-info');
  if (!productInfo) throw new Error('Missing product-info for cart edit');
  productInfo.querySelectorAll('script, modal-dialog, product-modal, pickup-availability').forEach((node) => node.remove());
  productInfo.removeAttribute('data-update-url');
  return productInfo;
}

function renderKokoCartEditModal(root, productInfo) {
  const content = getKokoCartEditModalContent();
  if (!content) return;

  const title = getKokoCartEditTitle(root, productInfo);
  const productUrl = getKokoCartEditProductUrl(root, true);
  const mediaMarkup = renderKokoCartEditMediaGallery(root, productInfo);
  const priceMarkup = getKokoCartEditPriceMarkup(root, productInfo);
  kokoCartEditSwatchStyles = getKokoCartEditPdpSwatchStyles(productInfo);
  content.innerHTML = `
    <div class="koko-cart-edit-modal__media" data-koko-cart-edit-product>
      ${mediaMarkup}
    </div>
    <div class="koko-cart-edit-modal__info">
      <a id="KokoCartEditModalTitle" href="${escapeKokoCartHtml(productUrl)}" class="koko-cart-edit__title">
        ${escapeKokoCartHtml(title)}
      </a>
      <div class="koko-cart-edit__price" data-koko-cart-edit-price>${priceMarkup}</div>
      ${renderKokoCartEditOptions(root)}
      <p class="koko-cart-edit__status" data-koko-cart-edit-status role="status"></p>
      <a class="underlined-link" data-koko-cart-edit-review href="${escapeKokoCartHtml(window.routes?.cart_url || '/cart')}" hidden>
        ${escapeKokoCartHtml(root.dataset.textReviewBag || '')}
      </a>
      <button type="button" class="button koko-cart-edit__update" data-koko-cart-edit-update>
        ${escapeKokoCartHtml(root.dataset.textUpdate || '')}
      </button>
      <a href="${escapeKokoCartHtml(productUrl)}" class="koko-cart-edit__details underlined-link">
        ${escapeKokoCartHtml(root.dataset.textSeeFullDetails || '')}
      </a>
    </div>
  `;
  initKokoCartEditMediaGallery(root);
  syncKokoCartEditState(root);
}

function closeKokoCartEdit(root = kokoCartEditActiveRoot) {
  const modal = getKokoCartEditModal();
  if (!modal || modal.hidden) return;

  root?.querySelector('[data-koko-cart-edit-open]')?.setAttribute('aria-expanded', 'false');
  modal.hidden = true;
  modal.classList.remove('is-loading');
  document.body.classList.remove('koko-cart-edit-lock');
  setKokoCartEditStatus(root);
  renderKokoCartEditLoading(root);
  const focusTarget = kokoCartEditReturnFocus;
  kokoCartEditActiveRoot = null;
  kokoCartEditReturnFocus = null;
  const drawer = root?.closest('cart-drawer.active');
  if (drawer) {
    trapFocus(drawer.querySelector('#CartDrawer'), focusTarget);
  } else {
    removeTrapFocus(focusTarget);
  }
}

async function openKokoCartEdit(root) {
  if (kokoCartEditUpdating) return;
  const modal = getKokoCartEditModal();
  if (!modal) return;

  if (kokoCartEditActiveRoot && kokoCartEditActiveRoot !== root) {
    kokoCartEditActiveRoot.querySelector('[data-koko-cart-edit-open]')?.setAttribute('aria-expanded', 'false');
  }

  kokoCartEditActiveRoot = root;
  kokoCartEditReturnFocus = root.querySelector('[data-koko-cart-edit-open]');
  root.dataset.selectedVariantId = root.dataset.currentVariantId || '';
  root.querySelector('[data-koko-cart-edit-open]')?.setAttribute('aria-expanded', 'true');
  modal.hidden = false;
  modal.classList.add('is-loading');
  document.body.classList.add('koko-cart-edit-lock');
  renderKokoCartEditLoading(root);
  trapFocus(modal, modal.querySelector('button[data-koko-cart-edit-close]'));

  try {
    const productInfo = await fetchKokoCartEditProduct(root);
    if (kokoCartEditActiveRoot !== root) return;
    renderKokoCartEditModal(root, productInfo);
  } catch (error) {
    console.error(error);
    if (kokoCartEditActiveRoot !== root) return;
    renderKokoCartEditModal(root, null);
    setKokoCartEditStatus(root, root.dataset.textError || '');
  } finally {
    if (kokoCartEditActiveRoot === root) {
      modal.classList.remove('is-loading');
      trapFocus(modal, modal.querySelector('[data-koko-cart-edit-value].is-selected, [data-koko-cart-edit-update]'));
    }
  }
}

function getKokoCartRenderer(root) {
  return (
    root.closest('cart-items') ||
    root.closest('cart-drawer-items') ||
    document.querySelector('cart-items') ||
    document.querySelector('cart-drawer-items')
  );
}

function getKokoCartSectionsToRender(renderer) {
  if (renderer && typeof renderer.getSectionsToRender === 'function') {
    return renderer.getSectionsToRender();
  }

  return [
    { id: 'main-cart-items', section: 'main-cart-items', selector: '.js-contents' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    { id: 'main-cart-footer', section: 'main-cart-footer', selector: '.js-contents' },
  ];
}

function renderKokoCartSections(parsedState, renderer) {
  if (!parsedState?.sections) {
    window.location.reload();
    return;
  }

  getKokoCartSectionsToRender(renderer).forEach((section) => {
    const sectionName = section.section || section.id;
    const html = parsedState.sections[sectionName];
    const container = document.getElementById(section.id);
    if (!html || !container) return;

    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
    const target = section.selector ? container.querySelector(section.selector) : container;
    if (source && target) target.innerHTML = source.innerHTML;
  });

  const isEmpty = parsedState.item_count === 0;
  document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);
  document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
  document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);
  renderKokoCartRecommendations();
  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'koko-cart-edit', cartData: parsedState });
}

function readKokoCartEditPending() {
  try {
    return JSON.parse(sessionStorage.getItem(kokoCartEditPendingKey) || 'null');
  } catch (_) {
    return null;
  }
}

function saveKokoCartEditPending(pending) {
  // Persist before a mutation. If storage is unavailable, do not start the request.
  sessionStorage.setItem(kokoCartEditPendingKey, JSON.stringify(pending));
}

function kokoCartEditLineSignature(item) {
  return JSON.stringify([
    String(item.variant_id),
    Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
    String(item.selling_plan_allocation?.selling_plan?.id || ''),
  ]);
}

async function readKokoCartEditCart() {
  const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
  if (!response.ok) throw new Error('Unable to verify bag');
  const cart = await response.json();
  if (!Array.isArray(cart.items) || !cart.token) throw new Error('Invalid bag response');
  return cart;
}

function getKokoCartEditRecovery(cart, pending) {
  const sourceSignature = kokoCartEditLineSignature(pending.source);
  const sources = cart.items.filter((item) => kokoCartEditLineSignature(item) === sourceSignature);
  const source = sources.find((item) => item.key === pending.source.key) || (sources.length === 1 ? sources[0] : null);
  const targetSignature = kokoCartEditLineSignature({ ...pending.source, variant_id: pending.variantId });
  const targetQuantity = cart.items
    .filter((item) => kokoCartEditLineSignature(item) === targetSignature)
    .reduce((sum, item) => sum + item.quantity, 0);
  return { source, sourceQuantity: sources.reduce((sum, item) => sum + item.quantity, 0), targetQuantity };
}

async function updateKokoCartVariant(root, button) {
  if (kokoCartEditUpdating) return;
  let pending = readKokoCartEditPending();
  const variant = findKokoCartSelectedVariant(root);
  if (!pending && (!variant || variant.available === false)) {
    syncKokoCartEditState(root);
    return;
  }
  if (!pending && String(variant.id) === root.dataset.currentVariantId) {
    closeKokoCartEdit(root);
    return;
  }

  kokoCartEditUpdating = true;
  syncKokoCartEditState(root);
  const renderer = getKokoCartRenderer(root);
  const sections = getKokoCartSectionsToRender(renderer).map((section) => section.section || section.id);
  let message = window.cartStrings?.error || '';

  try {
    let cart = await readKokoCartEditCart();
    if (pending && pending.token !== cart.token) {
      sessionStorage.removeItem(kokoCartEditPendingKey);
      throw new Error('Bag changed; review the current bag');
    }
    if (!pending) {
      const source = cart.items.find((item) => item.key === root.dataset.lineKey);
      if (!source || String(source.variant_id) !== root.dataset.currentVariantId || source.quantity !== Number(root.dataset.quantity)) {
        message = root.dataset.textReview;
        throw new Error('The original bag item changed');
      }
      pending = { token: cart.token, source, variantId: variant.id, options: variant.options, phase: 'adding' };
      const before = getKokoCartEditRecovery(cart, pending);
      pending.beforeQuantity = before.targetQuantity;
      pending.beforeSourceQuantity = before.sourceQuantity;
      pending.expectedSourceQuantity = before.sourceQuantity - source.quantity;
      pending.expectedQuantity = pending.beforeQuantity + source.quantity;
      saveKokoCartEditPending(pending);
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({
        id: variant.id, quantity: source.quantity, properties: source.properties || {},
        ...(source.selling_plan_allocation ? { selling_plan: source.selling_plan_allocation.selling_plan.id } : {}),
      });
      try {
        const response = await fetch(routes.cart_add_url, config);
        const added = await response.json();
        pending.phase = response.ok && !added.status ? 'added' : response.status >= 400 && response.status < 500 ? 'rejected' : 'adding';
        saveKokoCartEditPending(pending);
      } catch (error) {
        // An unknown response is not permission to repeat an additive request.
        console.error(error);
      }
      cart = await readKokoCartEditCart();
    }

    let recovery = getKokoCartEditRecovery(cart, pending);
    if (pending.phase === 'rejected' && recovery.targetQuantity === pending.beforeQuantity) {
      sessionStorage.removeItem(kokoCartEditPendingKey);
      throw new Error('Variant was not added');
    }
    const removalComplete = recovery.sourceQuantity === pending.expectedSourceQuantity;
    if (recovery.targetQuantity !== pending.expectedQuantity || (!removalComplete && (recovery.sourceQuantity !== pending.beforeSourceQuantity || !recovery.source || recovery.source.quantity !== pending.source.quantity))) {
      message = root.dataset.textReview;
      throw new Error('Bag update needs review; no further mutation performed');
    }
    if (!removalComplete) {
      if (String(pending.source.variant_id) !== root.dataset.currentVariantId) {
        message = root.dataset.textReview;
        throw new Error('Finish the previous item update first');
      }
      pending.phase = 'added';
      saveKokoCartEditPending(pending);
      // Only the removal remains. Absolute zero is safe to retry after readback.
      message = root.dataset.textPending;
      const config = fetchConfig('javascript');
      config.body = JSON.stringify({ id: recovery.source.key, quantity: 0, sections, sections_url: window.location.pathname });
      let changed;
      try {
        const response = await fetch(routes.cart_change_url, config);
        changed = await response.json();
        if (!response.ok || changed.errors || changed.status) throw new Error('Unable to remove original variant');
      } catch (error) {
        console.error(error);
      }
      cart = await readKokoCartEditCart();
      recovery = getKokoCartEditRecovery(cart, pending);
      if (recovery.sourceQuantity !== pending.expectedSourceQuantity || recovery.targetQuantity !== pending.expectedQuantity) throw new Error('Bag update is incomplete');
      if (changed?.sections) cart.sections = changed.sections;
    }
    sessionStorage.removeItem(kokoCartEditPendingKey);
    closeKokoCartEdit(root);
    renderKokoCartSections(cart, renderer);
  } catch (error) {
    console.error(error);
    if (readKokoCartEditPending() && message === window.cartStrings?.error) message = root.dataset.textReview;
  } finally {
    kokoCartEditUpdating = false;
    if (kokoCartEditActiveRoot) {
      syncKokoCartEditState(kokoCartEditActiveRoot);
      setKokoCartEditStatus(kokoCartEditActiveRoot, message);
    }
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cart-coupon-copy]');
      if (button) copyKokoCartCoupon(button);
    });
  }

  openMiniBag(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      renderKokoCartRecommendations();
    });

    this.addEventListener(
      'transitionend',
      () => {
        if (kokoCartEditActiveRoot) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  open(triggeredBy) {
    this.openMiniBag(triggeredBy);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    try {
      let sections = parsedState?.sections;
      if (!this.hasRenderableSections(sections)) sections = await this.fetchSections();
      if (!this.hasRenderableSections(sections)) {
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }

      this.classList.remove('is-empty');
      this.querySelector('.drawer__inner')?.classList.remove('is-empty');
      this.productId = parsedState?.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
      });

      await new Promise((resolve) => setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
        this.openMiniBag();
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      }));
    } catch (error) {
      console.error(error);
      window.location.assign(window.routes?.cart_url || '/cart');
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (source) return source.innerHTML;
    return null;
  }

  hasRenderableSections(sections) {
    return this.getSectionsToRender().every(
      (section) => this.getSectionInnerHTML(sections?.[section.id], section.selector) !== null
    );
  }

  async fetchSections() {
    const sectionUrl = new URL(window.location.href);
    sectionUrl.searchParams.set(
      'sections',
      this.getSectionsToRender()
        .map((section) => section.id)
        .join(',')
    );
    const response = await fetch(sectionUrl);
    if (!response.ok) return null;
    return response.json();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-koko-recommendation-add]');
  if (!button) return;
  addKokoCartRecommendation(button.dataset.kokoRecommendationAdd, button);
});

document.addEventListener('click', (event) => {
  const openButton = event.target.closest('[data-koko-cart-edit-open]');
  if (openButton) {
    event.preventDefault();
    const root = getKokoCartEditRoot(openButton);
    if (root) openKokoCartEdit(root);
    return;
  }

  const closeButton = event.target.closest('[data-koko-cart-edit-close]');
  if (closeButton) {
    event.preventDefault();
    closeKokoCartEdit();
    return;
  }

  const previousMediaButton = event.target.closest('[data-koko-cart-edit-media-prev]');
  if (previousMediaButton) {
    event.preventDefault();
    updateKokoCartEditMediaGallery(kokoCartEditActiveRoot, kokoCartEditMediaIndex - 1, 'smooth');
    return;
  }

  const nextMediaButton = event.target.closest('[data-koko-cart-edit-media-next]');
  if (nextMediaButton) {
    event.preventDefault();
    updateKokoCartEditMediaGallery(kokoCartEditActiveRoot, kokoCartEditMediaIndex + 1, 'smooth');
    return;
  }

  const valueButton = event.target.closest('[data-koko-cart-edit-value]');
  if (valueButton) {
    event.preventDefault();
    if (kokoCartEditUpdating || readKokoCartEditPending()) return;
    if (valueButton.getAttribute('aria-disabled') === 'true') return;
    const root = getKokoCartEditRoot(valueButton) || kokoCartEditActiveRoot;
    const option = valueButton.closest('[data-koko-cart-edit-option]');
    if (!root || !option) return;
    option.querySelectorAll('[data-koko-cart-edit-value]').forEach((button) => {
      const isSelected = button === valueButton;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });
    const selectedLabel = option.querySelector('[data-koko-cart-edit-selected]');
    if (selectedLabel) selectedLabel.textContent = valueButton.dataset.optionValue || '';
    if (valueButton.matches('[data-koko-cart-edit-color-index]')) {
      updateKokoCartEditMediaGallery(
        root,
        getKokoCartEditColorMediaIndex(root, valueButton.dataset.optionValue, kokoCartEditMediaItems)
      );
    }
    syncKokoCartEditState(root);
    return;
  }

  const updateButton = event.target.closest('[data-koko-cart-edit-update]');
  if (updateButton) {
    event.preventDefault();
    const root = getKokoCartEditRoot(updateButton) || kokoCartEditActiveRoot;
    if (root) updateKokoCartVariant(root, updateButton);
    return;
  }
});

document.addEventListener('keyup', (event) => {
  if (event.key !== 'Escape') return;
  closeKokoCartEdit();
});
